import { CourseTee, CourseTeeSnapshot } from '../types/Course';

/** Build a CourseTee-shaped object from a teeSnapshot so snapshot data can be
 *  used wherever a CourseTee is expected (e.g. tee pickers, scorecards). */
export function courseTeeFromSnapshot(teeId: string, snap: CourseTeeSnapshot): CourseTee {
  return {
    itemId: teeId,
    name: snap.name || snap.teeName || '',
    color: snap.color,
    color2: snap.color2,
    combo: snap.combo,
    holeColors: snap.holeColors,
    isForMen: snap.isForMen,
    isForWomen: snap.isForWomen,
    mensRating: snap.mensRating,
    mensSlope: snap.mensSlope,
    mensRatingFront9: snap.mensRatingFront9,
    mensRatingBack9: snap.mensRatingBack9,
    mensSlopeFront9: snap.mensSlopeFront9,
    mensSlopeBack9: snap.mensSlopeBack9,
    womensRating: snap.womensRating,
    womensSlope: snap.womensSlope,
    womensRatingFront9: snap.womensRatingFront9,
    womensRatingBack9: snap.womensRatingBack9,
    womensSlopeFront9: snap.womensSlopeFront9,
    womensSlopeBack9: snap.womensSlopeBack9,
    yardage: snap.yardage ?? [],
    par: snap.par,
    mensStrokeIndex: snap.mensStrokeIndex,
    womensStrokeIndex: snap.womensStrokeIndex,
    sortOrder: snap.sortOrder ?? 0,
    usgaMaleTeeSetRatingId: snap.usgaMaleTeeSetRatingId ?? null,
    usgaFemaleTeeSetRatingId: snap.usgaFemaleTeeSetRatingId ?? null,
  };
}

/** Every USGA TeeSetRatingId on a course's tees, both genders, deduped and sorted — stored as
 *  Course.usgaTeeSetRatingIds so courses can be queried by USGA tee set. Firestore rules also
 *  compare it to refuse non-God changes to tee-level USGA ids, so it includes tees of every
 *  status: changing a tee's status must not change the list. */
export function usgaTeeSetRatingIdsOf(tees: CourseTee[]): number[] {
  const ids = new Set<number>();
  for (const tee of tees) {
    if (tee.usgaMaleTeeSetRatingId != null) ids.add(tee.usgaMaleTeeSetRatingId);
    if (tee.usgaFemaleTeeSetRatingId != null) ids.add(tee.usgaFemaleTeeSetRatingId);
  }
  return [...ids].sort((a, b) => a - b);
}

/** True when a tee carries a USGA tee set id for either gender. Only God can change or remove it. */
export function isUsgaLinkedTee(tee: CourseTee): boolean {
  return (tee.usgaMaleTeeSetRatingId ?? null) != null || (tee.usgaFemaleTeeSetRatingId ?? null) != null;
}

/**
 * For a non-God tee CSV import: imported tees keep the USGA ids of the existing tee with the same
 * name, and ids in the file are ignored. `droppedLinkedTeeNames` lists USGA-linked tees the import
 * would remove — the caller refuses the import, since only God can remove them.
 */
export function carryUsgaTeeSetRatingIds(
  existing: CourseTee[],
  imported: CourseTee[],
): { tees: CourseTee[]; droppedLinkedTeeNames: string[] } {
  const byName = new Map(existing.map((t) => [t.name.trim().toLowerCase(), t]));
  const importedNames = new Set(imported.map((t) => t.name.trim().toLowerCase()));
  const tees = imported.map((t) => {
    const match = byName.get(t.name.trim().toLowerCase());
    return {
      ...t,
      usgaMaleTeeSetRatingId: match?.usgaMaleTeeSetRatingId ?? null,
      usgaFemaleTeeSetRatingId: match?.usgaFemaleTeeSetRatingId ?? null,
    };
  });
  const droppedLinkedTeeNames = existing
    .filter((t) => isUsgaLinkedTee(t) && !importedNames.has(t.name.trim().toLowerCase()))
    .map((t) => t.name);
  return { tees, droppedLinkedTeeNames };
}
